import React, { ChangeEventHandler, ReactNode, MouseEventHandler, useRef, useState } from 'react';

import './index.less';

import { getDefaultIfUndefined } from '../../../utils/typeUtil';
import IbButton from '../IbButton';
import IbIcon from '../IbIcon';

export enum IbInputType {
  Text = 'text',
  Password = 'password',
}

const STATUS_DEFAULT = 'default';
const MAIN_CLASS_NAME = 'ib-input';
const PREFIX_CLASS_NAME = `${MAIN_CLASS_NAME}__prefix`;
const SUFFIX_CLASS_NAME = `${MAIN_CLASS_NAME}__suffix`;
const SUFFIX_CLEAR_CLASS_NAME = `${SUFFIX_CLASS_NAME}__clear`;
const DISABLED_CLASS_NAME = `${MAIN_CLASS_NAME}_disabled`;

export interface IIbInputProps {
  className?: string;
  value?: null | string | number;
  status?: 'default' | 'success' | 'error';
  type?: IbInputType.Text | IbInputType.Password;
  disabled?: boolean;
  placeholder?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  allowClear?: boolean;
  autoFocus?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const IbInput: React.FC<IIbInputProps> = ({
  className,
  value,
  status = STATUS_DEFAULT,
  type = IbInputType.Text,
  disabled,
  placeholder,
  prefix,
  suffix,
  allowClear,
  autoFocus = false,
  onChange,
  onBlur,
  onKeyDown,
}) => {
  status = getDefaultIfUndefined(status, STATUS_DEFAULT);
  const inputRef = useRef<HTMLInputElement>(null);

  const [valueVisible, setValueVisible] = useState(type === IbInputType.Text);

  const classes = [MAIN_CLASS_NAME, `${MAIN_CLASS_NAME}_${status}`, disabled ? DISABLED_CLASS_NAME : null];
  className && classes.push(className);

  const onChangeInternal: ChangeEventHandler<HTMLInputElement> = (event) => onChange?.(event.target.value);
  const onDivClick: MouseEventHandler<HTMLDivElement> = (e) =>
    e.target === e.currentTarget && inputRef.current?.focus();

  const onClearInternal = () => onChange?.('');

  const onSwitchValueVisibility = () => setValueVisible(!valueVisible);

  const renderPreviewIcon = () => {
    return valueVisible ? <IbIcon iconName="preview-open" /> : <IbIcon iconName="preview-close-one" />;
  };

  const renderPrefix = () => {
    if (!prefix) {
      return null;
    }

    return <div className={PREFIX_CLASS_NAME}>{prefix}</div>;
  };

  const renderSuffix = () => {
    if (!suffix && !allowClear && !value) {
      return null;
    }

    return (
      <div className={SUFFIX_CLASS_NAME}>
        {allowClear && !!value && (
          <div className={SUFFIX_CLEAR_CLASS_NAME}>
            <IbButton icon={<IbIcon iconName="close" />} type="icon" onClick={onClearInternal}></IbButton>
          </div>
        )}
        {type === IbInputType.Password && (
          <div className={SUFFIX_CLEAR_CLASS_NAME}>
            <IbButton icon={renderPreviewIcon()} type="icon" onClick={onSwitchValueVisibility}></IbButton>
          </div>
        )}
        {suffix}
      </div>
    );
  };

  return (
    <div className={classes.join(' ')} onClick={onDivClick}>
      {renderPrefix()}
      <input
        ref={inputRef}
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder={placeholder}
        type={valueVisible ? IbInputType.Text : IbInputType.Password}
        value={value !== null ? value : undefined}
        onBlur={onBlur}
        onChange={onChangeInternal}
        onKeyDown={onKeyDown}
      />
      {renderSuffix()}
    </div>
  );
};

export default IbInput;
